import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, TrendingUp, DollarSign, Calendar } from "lucide-react";

interface HeroSectionProps {
  onRunScenario: () => void;
  defaultKpis?: {
    breakEvenMonth?: string;
    fiveYearOnPremTco?: number;
    fiveYearCloudSpend?: number;
  };
}

export const HeroSection = ({ onRunScenario, defaultKpis }: HeroSectionProps) => {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", { 
      style: "currency", 
      currency: "USD",
      notation: value > 1000000 ? "compact" : "standard",
      maximumFractionDigits: 1
    }).format(value);

  return (
    <div className="relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-hero opacity-10 animate-glow-pulse"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-secondary/5"></div>
      
      <div className="relative px-4 sm:px-6 py-12 sm:py-16 lg:py-24">
        <div className="mx-auto max-w-4xl text-center">
          {/* Main heading with animation */}
          <div className="animate-fade-up">           
            <h1 className="mb-4 sm:mb-6 text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight bg-gradient-to-r from-foreground via-primary to-secondary bg-clip-text text-transparent">
               See How Much You Can Save By Inferencing On-Prem vs. Cloud
            </h1>
            
            <p className="mb-6 sm:mb-8 text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
              Compare on-premises GenAI infrastructure costs vs cloud AIaaS with 
              <span className="text-primary font-semibold"> realistic workload simulation</span> and 
              discover your optimal investment strategy.
            </p>
          </div>

          {/* Preview stats */}
          {defaultKpis && (
            <div className="mb-8 sm:mb-12 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto px-4">
                <div className="bg-gradient-card p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-card border border-border/50 hover:shadow-elegant transition-smooth">
                  <div className="flex items-center justify-center mb-2 sm:mb-3">
                    <Calendar className="h-4 sm:h-6 w-4 sm:w-6 text-primary mr-1.5 sm:mr-2" />
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">Break-even Point</span>
                  </div>
                  <div className="text-lg sm:text-2xl font-bold text-foreground">
                    {defaultKpis.breakEvenMonth || "Month 18"}
                  </div>
                </div>
                
                <div className="bg-gradient-card p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-card border border-border/50 hover:shadow-elegant transition-smooth">
                  <div className="flex items-center justify-center mb-2 sm:mb-3">
                    <TrendingUp className="h-4 sm:h-6 w-4 sm:w-6 text-destructive mr-1.5 sm:mr-2" />
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">5-Year On-Prem TCO</span>
                  </div>
                  <div className="text-lg sm:text-2xl font-bold text-foreground">
                    {defaultKpis.fiveYearOnPremTco ? formatCurrency(defaultKpis.fiveYearOnPremTco) : "$2.4M"}
                  </div>
                </div>
                
                <div className="bg-gradient-card p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-card border border-border/50 hover:shadow-elegant transition-smooth">
                  <div className="flex items-center justify-center mb-2 sm:mb-3">
                    <DollarSign className="h-4 sm:h-6 w-4 sm:w-6 text-muted-foreground mr-1.5 sm:mr-2" />
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">5-Year Cloud Spend</span>
                  </div>
                  <div className="text-lg sm:text-2xl font-bold text-foreground">
                    {defaultKpis.fiveYearCloudSpend ? formatCurrency(defaultKpis.fiveYearCloudSpend) : "$3.1M"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CTA button */}
          <div className="animate-fade-up px-4" style={{ animationDelay: '0.4s' }}>
            <Button 
              size="lg" 
              onClick={onRunScenario}
              className="bg-gradient-primary hover:shadow-glow transition-smooth text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 h-auto font-semibold border-0 shadow-elegant w-full sm:w-auto"
            >
              <Sparkles className="mr-2 h-4 sm:h-5 w-4 sm:w-5" />
              Run My Scenario Details
            </Button>
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-muted-foreground">
              Customize your parameters below to see personalized results
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
